import React from 'react'

import { AssetDetailModal, SimpleTable, TableSkeleton, Alert } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  type ApiService,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatSeconds } from '@/utils/formatDate'

export interface ReeferRunTimeByAssetParams extends InsightParametersType {
  companyId?: number
  geofenceIds?: string[]
  geofenceCategoryIds?: string[]
  date?: string
  from?: string
  to?: string
}

type ReeferRunTimeTotals = {
  timeAtGeofenceSeconds: number
  reeferRunTimeSeconds: number
}

type ReeferRunTimeRow = {
  assetId: number
  assetName: string
} & ReeferRunTimeTotals

type ReeferRunTimeResponse = {
  data: ReeferRunTimeRow[]
  total: number
  totals: ReeferRunTimeTotals
}

type ReeferRunTimeData = ReeferRunTimeRow[] & { totals: ReeferRunTimeTotals }

const reportPath = '/insights/asset/reefer-run-time'

const ReeferRunTimeByAsset: InsightDefinition<ReeferRunTimeByAssetParams, ReeferRunTimeData> = {
  active: true,
  slug: 'asset/reefer-run-time-by-asset',
  category: 'asset',
  name: 'Reefer Run Time by Asset',
  description: 'Display reefer run time for each asset at selected geofences',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown' },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown' },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom.map((option) =>
          option.value === 'today' ? { ...option, isDefault: true } : option
        ),
      },
      {
        id: 'from',
        label: 'Date Range',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (parameters: ReeferRunTimeByAssetParams) => {
      if (!parameters.companyId) return 'Company is required.'

      const hasGeofences = Array.isArray(parameters.geofenceIds) && parameters.geofenceIds.length > 0
      const hasCategories = Array.isArray(parameters.geofenceCategoryIds) && parameters.geofenceCategoryIds.length > 0
      if (!hasGeofences && !hasCategories) {
        return 'Please select at least one Geofence or Geofence Category.'
      }

      if (parameters.date === 'custom') {
        if (!parameters.from || !parameters.to) {
          return 'Enter the custom date range.'
        }
        const fromDate = new Date(parameters.from)
        const toDate = new Date(parameters.to)
        if (fromDate > toDate) {
          return 'Start date must be before end date'
        }
        const dateValidation = validateDateRangeWithin30Days(fromDate.toISOString(), toDate.toISOString())
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: ReeferRunTimeByAssetParams) => {
    try {
      const response = await api.simpleGet<ReeferRunTimeResponse>({
        path: `${reportPath}?${buildQuery(parameters)}`,
        origin: 'ReeferRunTime',
      })
      if (!response.data?.length) {
        return { code: InsightStatusCode.NO_DATA }
      }
      return Object.assign([...response.data], { totals: response.totals })
    } catch (error) {
      return {
        code: InsightStatusCode.ERROR,
        message: error instanceof Error ? error.message : 'Unable to load reefer run time data',
      }
    }
  },

  csv: async (api: ApiService, parameters: ReeferRunTimeByAssetParams, title: string) => {
    await downloadCsv(api, reportPath, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = React.useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={3} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data) || !data?.length) {
      return null
    }

    const parameters = configuration.parameters as ReeferRunTimeByAssetParams
    const body = [
      ...data.map((row) => ({
        Asset: (
          <button
            type="button"
            onClick={() => setSelectedAsset(row.assetId)}
            className="text-left block w-full text-blue-500 cursor-pointer"
          >
            {row.assetName}
          </button>
        ),
        'Time at Geofence': formatSeconds(row.timeAtGeofenceSeconds),
        'Reefer Run Time': formatSeconds(row.reeferRunTimeSeconds),
      })),
      {
        Asset: <strong>Totals:</strong>,
        'Time at Geofence': formatSeconds(data.totals.timeAtGeofenceSeconds),
        'Reefer Run Time': formatSeconds(data.totals.reeferRunTimeSeconds),
      },
    ]

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={['Asset', 'Time at Geofence', 'Reefer Run Time']} body={body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration }) => {
    const { companies } = useCompany()
    const parameters = configuration.parameters as ReeferRunTimeByAssetParams
    const fields: Record<string, string> = {}

    const company = companies.find((item) => item.id === parameters.companyId)
    if (company) fields.Company = company.name
    addDateToCaptionFields({ ...parameters, fields, finite: true })
    return Object.keys(fields).length ? <Caption fields={fields} /> : ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA || !data?.length) {
      return <div>No records found</div>
    }
    return (
      <div>
        {data.length} row{data.length === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferRunTimeByAsset
